// Diff no-destructivo entre la propuesta del enriquecimiento (multi-fuente) y el
// estado actual del form de marca. El server NO escribe nada: acá calculamos qué
// cambia campo por campo y, al aceptar, mergeamos sobre el form reusando sus
// helpers (setField / setVoiceGuideField / addTag / addVoiceTag). El usuario
// sigue teniendo que apretar Save.

import { BrandEnrichmentFields, BrandVoiceGuide } from '@agency-smm/shared';
import {
  BrandFormData,
  BrandTagField,
  VoiceTagField,
  UseBrandFormResult,
} from '../../hooks/brand/useBrandForm';

export type EnrichGroup = 'Identity' | 'Personality' | 'Voice' | 'Content' | 'Contact';

/** Acción propuesta: rellenar un campo vacío, reemplazar uno ya escrito, o sumar items a una lista. */
export type EnrichAction = 'fill' | 'replace' | 'add';

export interface EnrichDiffItem {
  key: keyof BrandEnrichmentFields;
  label: string;
  group: EnrichGroup;
  type: 'string' | 'array';
  action: EnrichAction;
  current?: string;      // string fields
  suggested?: string;    // string fields
  newItems?: string[];   // array fields: items que no estaban
  accepted: boolean;     // tilde del usuario
}

// Definición de cada campo: dónde vive en el form (top-level o dentro del
// voiceGuide), su label en español y a qué grupo pertenece.
interface FieldDef {
  key: keyof BrandEnrichmentFields;
  label: string;
  group: EnrichGroup;
  type: 'string' | 'array';
  formKey?: keyof BrandFormData;       // campos top-level de brand_info
  voiceKey?: keyof BrandVoiceGuide;    // campos dentro del voiceGuide
}

const FIELDS: FieldDef[] = [
  { key: 'businessOverview', label: 'Business overview', group: 'Identity', type: 'string', formKey: 'businessOverview' },
  { key: 'coreIdentity', label: 'Core identity / mission', group: 'Identity', type: 'string', formKey: 'coreIdentity' },
  { key: 'marketPositioning', label: 'Positioning', group: 'Identity', type: 'string', formKey: 'marketPositioning' },
  { key: 'targetAudience', label: 'Target audience', group: 'Identity', type: 'string', formKey: 'targetAudience' },
  { key: 'customerProfile', label: 'Customer profile', group: 'Identity', type: 'string', formKey: 'customerProfile' },
  { key: 'competitors', label: 'Competitors', group: 'Identity', type: 'array', formKey: 'competitors' },
  { key: 'brandPersonality', label: 'Personality', group: 'Personality', type: 'string', formKey: 'brandPersonality' },
  { key: 'brandStory', label: 'Brand story', group: 'Personality', type: 'string', formKey: 'brandStory' },
  { key: 'emotionalBenefits', label: 'Emotional benefits', group: 'Personality', type: 'string', formKey: 'emotionalBenefits' },
  { key: 'productsServices', label: 'Products / services', group: 'Personality', type: 'array', formKey: 'productsServices' },
  { key: 'serviceAreas', label: 'Service areas', group: 'Personality', type: 'string', formKey: 'serviceAreas' },
  { key: 'phoneNumber', label: 'Phone', group: 'Contact', type: 'string', formKey: 'phoneNumber' },
  { key: 'address', label: 'Address', group: 'Contact', type: 'string', formKey: 'address' },
  { key: 'businessHours', label: 'Business hours', group: 'Contact', type: 'string', formKey: 'businessHours' },
  { key: 'whatsapp', label: 'WhatsApp', group: 'Contact', type: 'string', formKey: 'whatsapp' },
  { key: 'email', label: 'Email', group: 'Contact', type: 'string', formKey: 'email' },
  { key: 'postTopics', label: 'Post topics', group: 'Content', type: 'array', formKey: 'postTopics' },
  { key: 'keywords', label: 'Keywords', group: 'Content', type: 'array', formKey: 'keywords' },
  { key: 'voicePurpose', label: 'Voice purpose', group: 'Voice', type: 'string', voiceKey: 'purpose' },
  { key: 'voiceTone', label: 'Tone', group: 'Voice', type: 'string', voiceKey: 'tone' },
  { key: 'emotionalTone', label: 'Emotional tone', group: 'Voice', type: 'string', voiceKey: 'emotionalTone' },
  { key: 'voiceCharacter', label: 'Character', group: 'Voice', type: 'string', voiceKey: 'character' },
  { key: 'wordsToUse', label: 'Words to use', group: 'Voice', type: 'array', voiceKey: 'wordsToUse' },
  { key: 'wordsToAvoid', label: 'Words to avoid', group: 'Voice', type: 'array', voiceKey: 'wordsToAvoid' },
];

function currentString(def: FieldDef, form: BrandFormData): string {
  if (def.voiceKey) return ((form.voiceGuide?.[def.voiceKey] as string) || '').trim();
  return ((form[def.formKey!] as string) || '').trim();
}

function currentArray(def: FieldDef, form: BrandFormData): string[] {
  if (def.voiceKey) return (form.voiceGuide?.[def.voiceKey] as string[]) || [];
  return (form[def.formKey!] as string[]) || [];
}

/**
 * Calcula el diff entre la propuesta y el form. Devuelve solo los campos que
 * cambian algo. Pre-tilda lo aditivo (fill / add) y deja sin tildar los replace
 * (pisan datos ya escritos → decisión explícita del usuario).
 */
export function computeEnrichmentDiff(fields: BrandEnrichmentFields, form: BrandFormData): EnrichDiffItem[] {
  const out: EnrichDiffItem[] = [];
  for (const def of FIELDS) {
    const value = fields[def.key];
    if (def.type === 'string') {
      const suggested = typeof value === 'string' ? value.trim() : '';
      if (!suggested) continue;
      const current = currentString(def, form);
      if (!current) {
        out.push({ key: def.key, label: def.label, group: def.group, type: 'string', action: 'fill', current, suggested, accepted: true });
      } else if (current !== suggested) {
        out.push({ key: def.key, label: def.label, group: def.group, type: 'string', action: 'replace', current, suggested, accepted: false });
      }
    } else {
      const arr = Array.isArray(value) ? value.filter((x): x is string => typeof x === 'string' && !!x.trim()).map((x) => x.trim()) : [];
      if (!arr.length) continue;
      const current = currentArray(def, form);
      const lower = new Set(current.map((c) => c.toLowerCase()));
      const newItems = arr.filter((x) => !lower.has(x.toLowerCase()));
      if (newItems.length) {
        out.push({ key: def.key, label: def.label, group: def.group, type: 'array', action: 'add', newItems, accepted: true });
      }
    }
  }
  return out;
}

const FIELD_BY_KEY = new Map(FIELDS.map((f) => [f.key, f]));

/**
 * Aplica los items aceptados sobre el form usando sus helpers (que ya dedupean y
 * marcan dirty). Strings → setField / setVoiceGuideField. Arrays → addTag /
 * addVoiceTag por item.
 */
export function applyEnrichmentDiff(form: UseBrandFormResult, items: EnrichDiffItem[]): number {
  // setField es genérico; acá la llave es dinámica, así que la afinamos a una
  // firma permisiva una sola vez.
  const setField = form.setField as (field: keyof BrandFormData, value: string) => void;
  let applied = 0;

  for (const item of items) {
    if (!item.accepted) continue;
    const def = FIELD_BY_KEY.get(item.key);
    if (!def) continue;

    if (item.type === 'string' && item.suggested != null) {
      if (def.voiceKey) form.setVoiceGuideField(def.voiceKey, item.suggested);
      else if (def.formKey) setField(def.formKey, item.suggested);
      applied++;
    } else if (item.type === 'array' && item.newItems?.length) {
      for (const v of item.newItems) {
        if (def.voiceKey) form.addVoiceTag(def.voiceKey as VoiceTagField, v);
        else if (def.formKey) form.addTag(def.formKey as BrandTagField, v);
      }
      applied++;
    }
  }
  return applied;
}
